import React, { Component } from 'react';
import { Row, Container, Col, Form, Button, Modal, Spinner, Alert } from "react-bootstrap";
import { Pagination, TableHeader, TableBody, useDatatableLifecycle, shouldTableUpdate } from 'react-bs-datatable';
import { fetchProtocolsByPrimarySite, toSiteIdsParam } from '../lib/PrimarySiteUtils';

// The local datatable wrapper. The same block is copy-pasted into ten other
// screens (ProtocolMaster, MedicineMaster, Dashboard, ...) — this is the
// house convention, not a new one.
//
// Differs from those copies in two ways, both deliberate:
//  - no Filter row. The search box is not wanted on this popup.
//  - the table is not text-nowrap. Primary Sites can hold four site names
//    joined with ", ", which would otherwise push the table wider than the
//    modal and force horizontal scrolling; letting cells wrap keeps it inside
//    the page.
const CustomTable = React.memo(props => {
    const { data, tableHeaders, onPageNavigate, classes, onSortChange, tableClass, labels, rowsPerPage, currentPage, sortedProp, maxPage, Components } = useDatatableLifecycle(props);
    return (
        <div>
            <Components.Row>
                <Components.Col xs="12">
                    <Components.Table className={tableClass}>
                        <TableHeader
                            classes={classes}
                            tableHeaders={tableHeaders}
                            sortedProp={sortedProp}
                            onSortChange={onSortChange}
                            components={{
                                TableHead: Components.TableHead,
                                TableCell: Components.TableCell,
                                TableRow: Components.TableRow
                            }}
                        />
                        <TableBody
                            classes={classes}
                            tableHeaders={tableHeaders}
                            labels={labels}
                            data={data}
                            components={{
                                TableBody: Components.TableBody,
                                TableCell: Components.TableCell,
                                TableRow: Components.TableRow
                            }}
                        />
                    </Components.Table>
                </Components.Col>
            </Components.Row>
            <Components.Row className="controlRow__root bottom">
                <Components.Col xs={12} md={12} className="text-right">
                    <Pagination
                        classes={classes}
                        data={data}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        onPageNavigate={onPageNavigate}
                        labels={labels}
                        maxPage={maxPage}
                        components={{ Button: Components.Button, ButtonGroup: Components.ButtonGroup }}
                    />
                </Components.Col>
            </Components.Row>
        </div>
    );
}, shouldTableUpdate);

// No `filterable` flags: there is no filter box on this popup, so they would
// have nothing to drive. Sorting stays — it is how a long list is navigated
// here. The backend guarantees protocolName ascending, so the initial order
// matches what arrives.
const header = [
    { title: '', prop: 'pick', cellProps: { style: { width: '1%' } } },
    { title: 'Protocol', prop: 'protocol', sortable: true },
    { title: 'Primary Sites', prop: 'sites', sortable: true },
    { title: 'Duration', prop: 'duration', sortable: true },
    { title: 'Treatment Days', prop: 'days' },
];

const customLabels = { show: 'Display', entries: 'rows', noResults: 'No records found' };
const classes = { table: 'table-striped table-hover dataTable mt-2', thead: 'thead-light' };

// The canonical master spelling of the catch-all site, matched case- and
// whitespace-insensitively.
//
// Matched by NAME, never by id: the protocol_primary_site migration re-seeded
// primary_site_master and every previously-valid id silently changed meaning,
// so a hardcoded 60 in this file is exactly the kind of coupling that broke
// callers last time. The backend resolves this row by name for the same reason.
//
// Used only to decide whether to warn that the list is unfiltered — never sent.
const NOT_FOUND_SITE_NAME = 'not found';

// Confirm New Treatment Plan.
//
// Was a plain Yes/No prompt that made no backend call. It now also lists the
// protocols indicated for the diagnosis's primary sites, so the regimen is
// chosen here and carried into the treatment plan form.
//
// The original confirmation sentence is unchanged and still sits at the top;
// the table goes underneath it.
export default class ProtocolSelectionPopup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            protocolOptions: [],
            loading: false,
            error: '',
            // A protocolId, or the string 'NONE', or null for "nothing picked".
            // Ok stays disabled while this is null.
            selectedProtocolId: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show) {
            this.loadProtocols();
        }
    }

    loadProtocols() {
        const siteIdsParam = toSiteIdsParam(this.props.diagnosisPrimarySites);

        // Reset the selection on every open — a choice from a previous visit
        // must never be carried into a different treatment plan.
        this.setState({ loading: true, error: '', selectedProtocolId: null, protocolOptions: [] });

        fetchProtocolsByPrimarySite(siteIdsParam)
            .then((protocols) => {
                this.setState({ protocolOptions: protocols, loading: false });
            })
            .catch((err) => {
                // "None of these" stays available below, so a failed lookup
                // never blocks treatment-plan creation.
                this.setState({
                    loading: false,
                    protocolOptions: [],
                    error: 'Could not load the protocol list. You can still continue with "None of these".',
                });
            });
    }

    // True when the response is the unfiltered ~480-row set rather than a
    // recommendation. Both cases mean the same clinical thing — no primary site
    // is actually known — and the backend widens rather than narrows on either
    // (spec §7). Worth saying out loud, or the list looks like a bug.
    isUnfiltered() {
        const sites = Array.isArray(this.props.diagnosisPrimarySites) ? this.props.diagnosisPrimarySites : [];
        if (toSiteIdsParam(sites) === '') return true;
        return sites.some(
            (site) => site && String(site.siteName || '').trim().toLowerCase() === NOT_FOUND_SITE_NAME
        );
    }

    confirmationMessage() {
        const { firstName, lastName, diagnosisDate, diagnosisPrimarySite } = this.props;
        const patient = `${firstName} ${lastName}`;
        if (diagnosisDate) {
            return `Latest diagnosis is done on ${diagnosisDate}${diagnosisPrimarySite ? ` for ${diagnosisPrimarySite}` : ''}. Do you want to proceed to create a new treatment plan for ${patient}?`;
        }
        return `Do you want to proceed to create a new treatment plan for ${patient}?`;
    }

    handleOk = () => {
        const { selectedProtocolId, protocolOptions } = this.state;
        if (selectedProtocolId === null) return;

        if (selectedProtocolId === 'NONE') {
            // No protocol chosen, so the treatment plan form's Regimen dropdown
            // should offer everything. Done by sending NO siteIds at all rather
            // than the Not Found id: both widen identically, but an empty
            // parameter keeps a re-seedable master-table id out of our source.
            this.props.onConfirm({
                protocol: null,
                siteIdsForPlan: '',
                prefetchedProtocols: null,
            });
            return;
        }

        const protocol = protocolOptions.find(
            (p) => Number(p.protocolId) === Number(selectedProtocolId)
        );
        this.props.onConfirm({
            protocol: protocol
                ? { protocolId: protocol.protocolId, protocolName: protocol.protocolName }
                : null,
            siteIdsForPlan: toSiteIdsParam(this.props.diagnosisPrimarySites),
            // The plan form's dropdown needs the same list we just fetched, so
            // pass it along rather than making it ask again.
            prefetchedProtocols: protocolOptions,
        });
    };

    // Rebuilt on every render, which is load-bearing: react-bs-datatable's
    // shouldTableUpdate compares `tableBody` BY REFERENCE and does not look at
    // tableHeaders at all, so a memoised body would freeze the radio's checked
    // state on whatever was selected first.
    buildRows() {
        const { selectedProtocolId } = this.state;

        return this.state.protocolOptions.map((protocol) => {
            const id = protocol.protocolId;
            return {
                pick: (
                    <Form.Check
                        type="radio"
                        name="protocol-pick"
                        id={`protocol-pick-${id}`}
                        className="pl-0"
                        checked={Number(selectedProtocolId) === Number(id)}
                        onChange={() => this.setState({ selectedProtocolId: id })}
                    />
                ),
                protocol: protocol.protocolName || '',
                // Already joined by the backend, and never null — seven
                // protocols have no site mapped and come back as "".
                sites: protocol.primarySite || '--',
                // Null whenever no schedule is linked to the protocol.
                duration: protocol.protocolDuration || '--',
                // Null for single-day protocols.
                days: protocol.treatmentDays || '--',
            };
        });
    }

    render() {
        const { show, onHide } = this.props;
        const { loading, error, selectedProtocolId, protocolOptions } = this.state;

        return (
            <Modal show={show} onHide={onHide} centered size="lg" scrollable={true}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm New Treatment Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        <Row>
                            <Col lg='12' className="p-0">
                                {this.confirmationMessage()}
                            </Col>
                        </Row>

                        {error && (
                            <Row className="mt-3">
                                <Col lg='12' className="p-0">
                                    <Alert variant="warning" className="mb-0">{error}</Alert>
                                </Col>
                            </Row>
                        )}

                        {!error && !loading && this.isUnfiltered() && (
                            <Row className="mt-3">
                                <Col lg='12' className="p-0">
                                    <Alert variant="info" className="mb-0">
                                        No specific primary site is recorded for this diagnosis, so every
                                        protocol is listed. Sort by a column heading to find one.
                                    </Alert>
                                </Col>
                            </Row>
                        )}

                        <Row className="mt-3">
                            <Col lg='12' className="p-0">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status" />
                                        <div className="mt-2">Loading protocols…</div>
                                    </div>
                                ) : (
                                    <CustomTable
                                        id="datatableProtocolByPrimarySite"
                                        tableHeaders={header}
                                        tableBody={this.buildRows()}
                                        rowsPerPage={10}
                                        initialSort={{ prop: 'protocol', isAscending: true }}
                                        labels={customLabels}
                                        classes={classes}
                                    />
                                )}
                            </Col>
                        </Row>

                        {/* Outside the table on purpose. As a table row it would be
                            paginated away — with no primary site recorded the list
                            runs to hundreds of rows and this would land on the last
                            page. It is also the only enabled control when the list
                            is empty or the fetch failed, so it must always render. */}
                        <Row className="mt-2">
                            <Col lg='12' className="p-0">
                                <Form.Check
                                    type="radio"
                                    name="protocol-pick"
                                    id="protocol-pick-none"
                                    label="None of these"
                                    checked={selectedProtocolId === 'NONE'}
                                    onChange={() => this.setState({ selectedProtocolId: 'NONE' })}
                                />
                                {!loading && !error && protocolOptions.length === 0 && (
                                    <small className="text-muted d-block mt-1">
                                        No protocol is mapped to this primary site yet — choose
                                        "None of these" to pick a regimen on the next screen.
                                    </small>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={this.handleOk}
                        disabled={selectedProtocolId === null}
                    >
                        Ok
                    </Button>
                    <Button variant="outline-secondary" onClick={onHide}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
